import { ArrowSquareOut } from "@phosphor-icons/react";
import { useState } from "react";

import { MobileHeading } from "features/layout/components";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useWebhooks from "../../datahooks/useWebhooks";
import SettingsScrollContent from "../SettingsScrollContent";
import WebhooksDesktopTable from "./DesktopLayout/WebhooksDesktopTable";
import MobileList from "./MobileLayout/MobileList";
import NewWebhookButton from "./NewWebhookButton";

import EmptyPlaceholderImage from "assets/images/empty-placeholders/no-webhooks.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function Webhooks() {
  const isDesktop = useTwBreakpoint("lg");
  const [searchTerm, setSearchTerm] = useState("");

  const { webhooks, isLoadingWebhooks, isWebhooksError, refetchWebhooks } =
    useWebhooks();

  if (isWebhooksError) {
    return (
      <ErrorPlaceholder
        errorMessage="Could not display webhooks"
        onRetry={refetchWebhooks}
      />
    );
  } else if (isLoadingWebhooks) {
    return <Loader />;
  } else if (!webhooks?.length) {
    return (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No webhooks found"
        subtitle="Add a webhook to connect Aimfox with your application"
        actionComponent={<NewWebhookButton size="md" />}
      />
    );
  }

  const filteredWebhooks = webhooks.filter((webhook) =>
    webhook.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const searchInputComponent = (
    <SearchInput
      placeholder="Search webhooks"
      value={searchTerm}
      className="lg:max-w-72"
      variant={isDesktop ? "sm" : "lg"}
      onChange={(e) => setSearchTerm(e.target.value)}
      onClear={() => setSearchTerm("")}
    />
  );

  const actionComponent = (
    <>
      <Button
        variant="secondary-black"
        leftIcon={<ArrowSquareOut />}
        size={isDesktop ? "sm" : "md"}
        className="max-lg:order-2 max-lg:ml-auto"
        asChild
      >
        <a
          href="https://docs.aimfox.com/webhooks"
          target="_blank"
          rel="noreferrer"
        >
          Documentation
        </a>
      </Button>
      <NewWebhookButton size={isDesktop ? "sm" : "md"} />
    </>
  );

  const Component = isDesktop ? WebhooksDesktopTable : MobileList;

  return (
    <SettingsScrollContent desktopActionComponent={actionComponent}>
      <MobileHeading
        actionComponent={actionComponent}
        searchComponent={searchInputComponent}
      />
      {isDesktop && searchInputComponent}
      {filteredWebhooks.length ? (
        <Component webhooks={filteredWebhooks} />
      ) : (
        <EmptyPlaceholder
          title="No webhooks found"
          subtitle="No webhooks match your search criteria. Try adjusting your search"
          imageSrc={NoResultsImage}
          actionComponent={
            <Button onClick={() => setSearchTerm("")}>Clear search</Button>
          }
        />
      )}
    </SettingsScrollContent>
  );
}
